import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Tag,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
  UnorderedList,
  ListItem,
  Stack,
} from '@chakra-ui/react';
import { useGetEquipmentsQuery, useDeleteEquipmentMutation, useModalitiesQuery } from '@/app/services/nucleus';
import { useState, useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { BsPencilSquare, BsPlus, BsTrash } from 'react-icons/bs';
import EquipmentModal from './EquipmentModal';
import ModalComponent from '@/features/common/ModalComponent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TableButton from '../common/table/TableButton';
import { usePagination } from '@/hooks/usePagination.js';
import { useServerSideSorting } from '@/hooks/useServerSideSorting.js';
import SSDataTable from '@/features/common/table/SSDataTable.jsx';
import { isRealValue } from '@/utils/helpers.js';
import SearchInput from '@/features/common/SearchInput.jsx';
import { useDebouncedSearchSync } from '@/hooks/useDebouncedSearchSync.js';
import AlertComponent from '../common/AlertComponent';
import TruncatedArrayComponent from '@/features/common/TruncatedArrayComponent';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';

const EquipmentPage = () => {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();

  const [searchText, setSearchText] = useState('');
  const [selectedModality, setSelectedModality] = useState(null);
  const [pageCount, setPageCount] = useState();
  const [urlParamsParsed, setUrlParamsParsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [pendingDeleteEquipment, setPendingDeleteEquipment] = useState(null);

  const {
    limit,
    onPaginationChange,
    pagination: paginationState,
    offset,
  } = usePagination({
    onPageChange: (pageState) => {
      setSearchParams((params) => {
        params.set('page', pageState.pageIndex);
        params.set('limit', pageState.pageSize);
        return params;
      });
    },
  });

  const { sorting, onSortingChange, sortByField } = useServerSideSorting([{ id: 'name', desc: false }]);

  const { data: modalities } = useModalitiesQuery();

  const modalityOptions = useMemo(() => {
    return (modalities?.items || []).map((m) => ({ value: m.id, label: m.short_name }));
  }, [modalities?.items]);

  const {
    data: equipments,
    isLoading,
    isError,
    error: equipmentsError,
  } = useGetEquipmentsQuery(
    {
      search: searchText,
      sortBy: sortByField,
      limit,
      offset,
      modalityId: selectedModality?.value,
    },
    { skip: !urlParamsParsed },
  );

  useDebouncedSearchSync({ searchText, onPaginationChange, limit, setSearchParams });

  useEffect(() => {
    if (isRealValue(equipments?.total)) {
      setPageCount(Math.ceil(equipments?.total / limit));
    }
  }, [equipments?.total, limit]);

  useEffect(() => {
    const pageState = { ...paginationState };
    for (let param of searchParams) {
      if (Array.isArray(param) && param.at(0) === 'search') {
        setSearchText(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'page') {
        pageState.pageIndex = Number(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'limit') {
        pageState.pageSize = Number(param.at(1));
      }
    }
    onPaginationChange(pageState);
    setUrlParamsParsed(true);
  }, []);

  const [deleteEquipment] = useDeleteEquipmentMutation();

  const handleEditEquipment = (id) => {
    navigate(`/equipment/${id}`);
  };

  const handleDeleteEquipment = (id, name) => {
    setPendingDeleteEquipment({ id, name });
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    deleteEquipment(pendingDeleteEquipment.id);
    onDeleteClose();
  };

  const handleSearchTextChange = (event) => {
    if (event.target.value === '') {
      handleClearSearchText();
    } else {
      setSearchText(event.target.value);
    }
  };

  const handleClearSearchText = () => {
    setSearchText('');
    searchParams.delete('search');
    setSearchParams(searchParams);
  };

  const handleNewEquipment = () => {
    onOpen();
  };

  const handleModalityChange = (option) => {
    setSelectedModality(option);
    onPaginationChange({ pageSize: limit, pageIndex: 0 });
  };

  const equipmentColumns = [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('brand', { header: 'Brand' }),
    columnHelper.accessor('modalities', {
      header: 'Modalities',
      disableSortBy: true,
      cell: ({ getValue }) => {
        const items = getValue() || [];
        const truncatedItems = items.slice(0, 3);
        const truncatedString = truncatedItems.map((m) => m.short_name).join(', ');
        const row = { modalitiesPopover: items, modalitiesTruncated: truncatedString, modalitiesTruncatedList: truncatedItems };
        return (
          <TruncatedArrayComponent
            title="Modalities"
            fieldName="modalities"
            row={row}
            renderTruncated={(list) =>
              list.map((m) => (
                <Tag key={m.id} size="sm" colorScheme="purple" borderRadius="full">
                  {m.short_name}
                </Tag>
              ))
            }
            renderItems={(list) => (
              <UnorderedList>
                {list.map((m) => (
                  <ListItem key={m.id}>{m.short_name}</ListItem>
                ))}
              </UnorderedList>
            )}
          />
        );
      },
    }),
    columnHelper.accessor('category', {
      header: 'Category',
      cell: ({ getValue }) => {
        const cat = getValue();
        if (!cat) return null;
        if (cat.deleted_at) {
          return (
            <Tooltip hasArrow placement="top" label="This category has been deleted">
              <Text as="s" color="gray.400" display="inline">
                {cat.name}
              </Text>
            </Tooltip>
          );
        }
        return cat.name;
      },
    }),
    columnHelper.accessor('', {
      header: 'Action',
      disableSortBy: true,
      cell: (info) => {
        const equipment = info.row.original;
        return (
          <HStack>
            <Tooltip hasArrow placement="top" label="Edit Equipment">
              <Box p="0" as="span">
                <TableButton aria-label="edit" icon={<BsPencilSquare />} onClick={() => handleEditEquipment(equipment?.id)} />
              </Box>
            </Tooltip>
            <Tooltip hasArrow placement="top" label="Delete Equipment">
              <Box p="0" as="span">
                <TableButton aria-label="delete" icon={<BsTrash />} onClick={() => handleDeleteEquipment(equipment?.id, equipment?.name)} />
              </Box>
            </Tooltip>
          </HStack>
        );
      },
    }),
  ];

  return (
    <Box data-test-id="pact-equipment-page">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Equipment
      </Heading>
      <Stack direction={['column', 'row']} minWidth="max-content" alignItems="center" gap="0">
        <Box p={5} width={{ base: '100%', md: '50%' }}>
          <SearchInput isDisabled={false} searchText={searchText} dataType="text" onChange={handleSearchTextChange} onClear={handleClearSearchText} />
        </Box>
        <Flex gap={5} justifyContent="space-between" width="100%">
          <Select
            chakraStyles={chakraStyles}
            focusBorderColor="brand.700"
            options={modalityOptions}
            value={selectedModality}
            onChange={handleModalityChange}
            isClearable
            isSearchable
            placeholder="Filter by Modality"
          />
          <Spacer />
          <Button leftIcon={<BsPlus />} onClick={handleNewEquipment} variant="purple" type="button">
            New
          </Button>
        </Flex>
      </Stack>
      {isError ? (
        <AlertComponent status="warning" title="Equipment Error" description={equipmentsError?.data?.detail ?? 'Error fetching equipment'} />
      ) : (
        <SSDataTable
          loading={isLoading}
          data={equipments?.items}
          total={equipments?.total ?? 0}
          columns={equipmentColumns}
          pagination={paginationState}
          pageCount={pageCount}
          onPaginationChange={onPaginationChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      )}
      <EquipmentModal isOpen={isOpen} onClose={onClose} onSave={onClose} />
      <ModalComponent
        size="sm"
        title="Delete Equipment"
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={handleConfirmDelete}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        isError={false}
      >
        <Box>
          <Text>
            Are you sure you want to delete <strong>{pendingDeleteEquipment?.name}</strong>?
          </Text>
        </Box>
      </ModalComponent>
    </Box>
  );
};

export default EquipmentPage;
